import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle, 
  DialogTrigger 
} from '../components/ui/dialog';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import { 
  BookOpen, 
  GraduationCap, 
  Plus, 
  Calendar,
  User,
  BarChart3,
  Trash2,
  Clock,
  Edit,
  AlertTriangle,
  FileWarning,
  TrendingUp,
  Award
} from 'lucide-react';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TIPI_NOTA = [
  { value: 'NOTA_DISCIPLINARE', label: 'Nota Disciplinare', color: 'bg-orange-100 text-orange-800' },
  { value: 'NOTA_DIDATTICA', label: 'Nota Didattica', color: 'bg-blue-100 text-blue-800' },
  { value: 'ALTRO', label: 'Altro tipo di annotazione', color: 'bg-slate-100 text-slate-800' },
  { value: 'SOSPENSIONE', label: 'Sospensione', color: 'bg-red-100 text-red-800' },
  { value: 'ESPULSIONE', label: 'Espulsione', color: 'bg-red-200 text-red-900' }
];

const STATO_NOTA = {
  ATTIVA: 'bg-red-100 text-red-800',
  REVOCATA: 'bg-green-100 text-green-800',
  COMPLETATA: 'bg-slate-100 text-slate-800'
};

const Scuola = () => {
  const { user, hasPermesso, isSindacatoAdmin } = useAuth();
  const [lezioni, setLezioni] = useState([]);
  const [voti, setVoti] = useState([]);
  const [noteDisciplinari, setNoteDisciplinari] = useState([]);
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [votoDialogOpen, setVotoDialogOpen] = useState(false);
  const [notaDialogOpen, setNotaDialogOpen] = useState(false);
  const [editVotoDialogOpen, setEditVotoDialogOpen] = useState(false);
  const [selectedVoto, setSelectedVoto] = useState(null);
  
  // Dati studente
  const [pagella, setPagella] = useState(null);
  const [mieiVoti, setMieiVoti] = useState([]);
  const [mieNote, setMieNote] = useState([]);
  
  const [lezioneForm, setLezioneForm] = useState({
    materia: '',
    argomento: '',
    data_lezione: new Date().toISOString().split('T')[0],
    note: ''
  });
  const [votoForm, setVotoForm] = useState({
    studente_id: '',
    materia: '',
    voto: '',
    descrizione: '',
    data_voto: new Date().toISOString().split('T')[0]
  });
  const [notaForm, setNotaForm] = useState({
    studente_id: '',
    tipo: 'NOTA_DISCIPLINARE',
    motivo: '',
    giorni_sospensione: ''
  });

  const canFirmaLezioni = () => isSindacatoAdmin() || hasPermesso('firma_lezioni');
  const canDirezione = () => isSindacatoAdmin() || hasPermesso('direzione_scuola');
  const canGestisciVoti = () => isSindacatoAdmin() || hasPermesso('gestisci_voti');
  const isStaff = () => canFirmaLezioni() || canDirezione() || canGestisciVoti();

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const promises = [
        axios.get(`${API_URL}/scuola/lezioni`),
        axios.get(`${API_URL}/scuola/pagella`),
        axios.get(`${API_URL}/scuola/miei-voti`),
        axios.get(`${API_URL}/scuola/mie-note`)
      ];
      
      if (canDirezione()) {
        promises.push(axios.get(`${API_URL}/scuola/lezioni/stats`));
      }
      
      if (canGestisciVoti() || canDirezione()) {
        promises.push(axios.get(`${API_URL}/scuola/voti`));
        promises.push(axios.get(`${API_URL}/users`));
        promises.push(axios.get(`${API_URL}/scuola/note-disciplinari`));
      }
      
      const results = await Promise.all(promises);
      setLezioni(results[0].data);
      setPagella(results[1].data);
      setMieiVoti(results[2].data);
      setMieNote(results[3].data);
      
      let idx = 4;
      if (canDirezione() && results[idx]) {
        setStats(results[idx].data);
        idx++;
      }
      
      if (results[idx]) setVoti(results[idx].data);
      if (results[idx + 1]) setUsers(results[idx + 1].data);
      if (results[idx + 2]) setNoteDisciplinari(results[idx + 2].data);
    } catch (error) {
      console.error('Error fetching data:', error);
      if (error.response?.status !== 403) {
        toast.error('Errore nel caricamento dei dati');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleFirmaLezione = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API_URL}/scuola/lezioni`, lezioneForm);
      toast.success('Lezione firmata con successo');
      setDialogOpen(false);
      setLezioneForm({
        materia: '',
        argomento: '',
        data_lezione: new Date().toISOString().split('T')[0],
        note: ''
      });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nella firma della lezione');
    }
  };

  const handleInsertVoto = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API_URL}/scuola/voti`, {
        ...votoForm,
        voto: parseFloat(votoForm.voto)
      });
      toast.success('Voto inserito con successo');
      setVotoDialogOpen(false);
      setVotoForm({
        studente_id: '',
        materia: '',
        voto: '',
        descrizione: '',
        data_voto: new Date().toISOString().split('T')[0]
      });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nell\'inserimento del voto');
    }
  };

  const handleInsertNota = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API_URL}/scuola/note-disciplinari`, {
        ...notaForm,
        giorni_sospensione: notaForm.tipo === 'SOSPENSIONE' ? parseInt(notaForm.giorni_sospensione) : null
      });
      toast.success('Nota disciplinare inserita');
      setNotaDialogOpen(false);
      setNotaForm({
        studente_id: '',
        tipo: 'NOTA_DISCIPLINARE',
        motivo: '',
        giorni_sospensione: ''
      });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nell\'inserimento della nota');
    }
  };

  const handleDeleteLezione = async (id) => {
    if (!window.confirm('Eliminare questa lezione?')) return;
    try {
      await axios.delete(`${API_URL}/scuola/lezioni/${id}`);
      toast.success('Lezione eliminata');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nell\'eliminazione');
    }
  };

  const handleDeleteVoto = async (id) => {
    if (!window.confirm('Eliminare questo voto?')) return;
    try {
      await axios.delete(`${API_URL}/scuola/voti/${id}`);
      toast.success('Voto eliminato');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nell\'eliminazione');
    }
  };

  const handleDeleteNota = async (id) => {
    if (!window.confirm('Eliminare questa nota disciplinare?')) return;
    try {
      await axios.delete(`${API_URL}/scuola/note-disciplinari/${id}`);
      toast.success('Nota eliminata');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nell\'eliminazione');
    }
  };

  const handleRevokeNota = async (id) => {
    try {
      await axios.patch(`${API_URL}/scuola/note-disciplinari/${id}?stato=REVOCATA`);
      toast.success('Nota revocata');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore');
    }
  };

  const handleEditVoto = async (e) => {
    e.preventDefault();
    try {
      const params = new URLSearchParams();
      if (selectedVoto.voto) params.append('voto', selectedVoto.voto);
      if (selectedVoto.descrizione) params.append('descrizione', selectedVoto.descrizione);
      
      await axios.patch(`${API_URL}/scuola/voti/${selectedVoto.id}?${params.toString()}`);
      toast.success('Voto aggiornato');
      setEditVotoDialogOpen(false);
      setSelectedVoto(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nella modifica');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="scuola-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Gestione Scuola</h1>
          <p className="text-slate-600">Registro lezioni, voti e note disciplinari</p>
        </div>
        <div className="flex flex-wrap gap-2">
          {canFirmaLezioni() && (
            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
              <DialogTrigger asChild>
                <Button data-testid="firma-lezione-btn">
                  <Plus className="w-4 h-4 mr-2" />
                  Firma Lezione
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Firma Lezione</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleFirmaLezione} className="space-y-4">
                  <div className="space-y-2">
                    <Label>Materia</Label>
                    <Input
                      value={lezioneForm.materia}
                      onChange={(e) => setLezioneForm({...lezioneForm, materia: e.target.value})}
                      placeholder="es. Matematica"
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Argomento</Label>
                    <Textarea
                      value={lezioneForm.argomento}
                      onChange={(e) => setLezioneForm({...lezioneForm, argomento: e.target.value})}
                      placeholder="Argomento trattato..."
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Data Lezione</Label>
                    <Input
                      type="date"
                      value={lezioneForm.data_lezione}
                      onChange={(e) => setLezioneForm({...lezioneForm, data_lezione: e.target.value})}
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Note (opzionale)</Label>
                    <Textarea
                      value={lezioneForm.note}
                      onChange={(e) => setLezioneForm({...lezioneForm, note: e.target.value})}
                      placeholder="Note aggiuntive..."
                    />
                  </div>
                  <div className="flex justify-end gap-2">
                    <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Annulla</Button>
                    <Button type="submit">Firma Lezione</Button>
                  </div>
                </form>
              </DialogContent>
            </Dialog>
          )}
          
          {canGestisciVoti() && (
            <>
              <Dialog open={votoDialogOpen} onOpenChange={setVotoDialogOpen}>
                <DialogTrigger asChild>
                  <Button variant="outline" data-testid="inserisci-voto-btn">
                    <GraduationCap className="w-4 h-4 mr-2" />
                    Inserisci Voto
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Inserisci Voto</DialogTitle>
                  </DialogHeader>
                  <form onSubmit={handleInsertVoto} className="space-y-4">
                    <div className="space-y-2">
                      <Label>Studente</Label>
                      <select
                        className="w-full border rounded-md p-2"
                        value={votoForm.studente_id}
                        onChange={(e) => setVotoForm({...votoForm, studente_id: e.target.value})}
                        required
                      >
                        <option value="">Seleziona studente...</option>
                        {users.map(u => (
                          <option key={u.id} value={u.id}>{u.nome} {u.cognome}</option>
                        ))}
                      </select>
                    </div>
                    <div className="space-y-2">
                      <Label>Materia</Label>
                      <Input
                        value={votoForm.materia}
                        onChange={(e) => setVotoForm({...votoForm, materia: e.target.value})}
                        placeholder="es. Matematica"
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Voto (1-10)</Label>
                      <Input
                        type="number"
                        min="1"
                        max="10"
                        step="0.5"
                        value={votoForm.voto}
                        onChange={(e) => setVotoForm({...votoForm, voto: e.target.value})}
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Descrizione</Label>
                      <Input
                        value={votoForm.descrizione}
                        onChange={(e) => setVotoForm({...votoForm, descrizione: e.target.value})}
                        placeholder="es. Verifica scritta"
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Data</Label>
                      <Input
                        type="date"
                        value={votoForm.data_voto}
                        onChange={(e) => setVotoForm({...votoForm, data_voto: e.target.value})}
                        required
                      />
                    </div>
                    <div className="flex justify-end gap-2">
                      <Button type="button" variant="outline" onClick={() => setVotoDialogOpen(false)}>Annulla</Button>
                      <Button type="submit">Inserisci Voto</Button>
                    </div>
                  </form>
                </DialogContent>
              </Dialog>

              <Dialog open={notaDialogOpen} onOpenChange={setNotaDialogOpen}>
                <DialogTrigger asChild>
                  <Button variant="destructive" data-testid="nota-disciplinare-btn">
                    <FileWarning className="w-4 h-4 mr-2" />
                    Nota Disciplinare
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Inserisci Nota Disciplinare</DialogTitle>
                  </DialogHeader>
                  <form onSubmit={handleInsertNota} className="space-y-4">
                    <div className="space-y-2">
                      <Label>Studente</Label>
                      <select
                        className="w-full border rounded-md p-2"
                        value={notaForm.studente_id}
                        onChange={(e) => setNotaForm({...notaForm, studente_id: e.target.value})}
                        required
                      >
                        <option value="">Seleziona studente...</option>
                        {users.map(u => (
                          <option key={u.id} value={u.id}>{u.nome} {u.cognome}</option>
                        ))}
                      </select>
                    </div>
                    <div className="space-y-2">
                      <Label>Tipo</Label>
                      <select
                        className="w-full border rounded-md p-2"
                        value={notaForm.tipo}
                        onChange={(e) => setNotaForm({...notaForm, tipo: e.target.value})}
                      >
                        {TIPI_NOTA.map(t => (
                          <option key={t.value} value={t.value}>{t.label}</option>
                        ))}
                      </select>
                    </div>
                    {notaForm.tipo === 'SOSPENSIONE' && (
                      <div className="space-y-2">
                        <Label>Giorni di Sospensione</Label>
                        <Input
                          type="number"
                          min="1"
                          max="30"
                          value={notaForm.giorni_sospensione}
                          onChange={(e) => setNotaForm({...notaForm, giorni_sospensione: e.target.value})}
                          required
                        />
                      </div>
                    )}
                    <div className="space-y-2">
                      <Label>Motivo</Label>
                      <Textarea
                        value={notaForm.motivo}
                        onChange={(e) => setNotaForm({...notaForm, motivo: e.target.value})}
                        placeholder="Descrivi il motivo della nota..."
                        required
                      />
                    </div>
                    <div className="flex justify-end gap-2">
                      <Button type="button" variant="outline" onClick={() => setNotaDialogOpen(false)}>Annulla</Button>
                      <Button type="submit" variant="destructive">Inserisci Nota</Button>
                    </div>
                  </form>
                </DialogContent>
              </Dialog>
            </>
          )}
        </div>
      </div>

      {/* Statistiche per la Direzione */}
      {canDirezione() && stats && (
        <Card className="bg-gradient-to-r from-blue-600 to-blue-800 text-white">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <BarChart3 className="w-5 h-5" />
              Statistiche Direzione
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="bg-white/10 rounded-lg p-4">
                <p className="text-sm text-blue-100">Totale Lezioni Firmate</p>
                <p className="text-3xl font-bold">{stats.totale_lezioni}</p>
              </div>
              <div className="bg-white/10 rounded-lg p-4 col-span-2">
                <p className="text-sm text-blue-100 mb-2">Lezioni per Docente</p>
                <div className="space-y-1 max-h-32 overflow-y-auto">
                  {stats.per_docente?.map((d, i) => (
                    <div key={i} className="flex justify-between text-sm">
                      <span>{d.docente_nome}</span>
                      <Badge className="bg-white/20">{d.lezioni} lezioni</Badge>
                    </div>
                  ))}
                  {stats.per_docente?.length === 0 && (
                    <p className="text-sm text-blue-200">Nessuna lezione ancora firmata</p>
                  )}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Tabs */}
      <Tabs defaultValue={isStaff() ? "lezioni" : "pagella"} className="w-full">
        <TabsList>
          {/* Tab Studente */}
          <TabsTrigger value="pagella" className="gap-2">
            <Award className="w-4 h-4" />
            La Mia Pagella
          </TabsTrigger>
          <TabsTrigger value="lezioni" className="gap-2">
            <BookOpen className="w-4 h-4" />
            Registro Lezioni
          </TabsTrigger>
          {canGestisciVoti() && (
            <>
              <TabsTrigger value="voti" className="gap-2">
                <GraduationCap className="w-4 h-4" />
                Voti
              </TabsTrigger>
              <TabsTrigger value="disciplina" className="gap-2">
                <AlertTriangle className="w-4 h-4" />
                Note Disciplinari
              </TabsTrigger>
            </>
          )}
        </TabsList>

        {/* La Mia Pagella (per studenti) */}
        <TabsContent value="pagella" className="mt-6">
          {pagella && (
            <div className="space-y-6">
              {/* Media Generale */}
              <Card className="bg-gradient-to-r from-amber-500 to-orange-500 text-white">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-amber-100 text-sm">Media Generale</p>
                      <p className="text-5xl font-bold">{pagella.media_generale}</p>
                      <p className="text-amber-100 text-sm mt-1">{pagella.totale_voti} voti totali</p>
                    </div>
                    <Award className="w-16 h-16 text-white/30" />
                  </div>
                </CardContent>
              </Card>

              {/* Pagella per Materia */}
              {pagella.materie.length > 0 ? (
                <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                  {pagella.materie.map((materia, idx) => (
                    <Card key={idx} className="hover:shadow-md transition-shadow">
                      <CardHeader className="pb-2">
                        <div className="flex justify-between items-center">
                          <CardTitle className="text-lg">{materia.materia}</CardTitle>
                          <Badge className={materia.media >= 6 ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}>
                            Media: {materia.media}
                          </Badge>
                        </div>
                      </CardHeader>
                      <CardContent>
                        <p className="text-sm text-slate-500 mb-2">{materia.numero_voti} voti</p>
                        <div className="space-y-1">
                          {materia.voti.slice(-3).map((v, i) => (
                            <div key={i} className="flex justify-between text-sm">
                              <span className="text-slate-600">{v.descrizione}</span>
                              <Badge variant="outline">{v.voto}</Badge>
                            </div>
                          ))}
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              ) : (
                <Card className="border-dashed">
                  <CardContent className="flex flex-col items-center justify-center py-12">
                    <GraduationCap className="w-12 h-12 text-slate-300 mb-4" />
                    <p className="text-slate-500">Nessun voto ancora registrato</p>
                  </CardContent>
                </Card>
              )}

              {/* Le Mie Note */}
              {mieNote.length > 0 && (
                <div>
                  <h3 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
                    <AlertTriangle className="w-5 h-5 text-amber-500" />
                    Le Mie Note
                  </h3>
                  <div className="space-y-2">
                    {mieNote.map((nota) => {
                      const tipoConfig = TIPI_NOTA.find(t => t.value === nota.tipo);
                      return (
                        <Card key={nota.id} className={nota.stato === 'REVOCATA' ? 'opacity-60' : ''}>
                          <CardContent className="p-4">
                            <div className="flex items-center gap-2 mb-2">
                              <Badge className={tipoConfig?.color || 'bg-slate-100'}>
                                {tipoConfig?.label || nota.tipo}
                              </Badge>
                              <Badge className={STATO_NOTA[nota.stato]}>
                                {nota.stato}
                              </Badge>
                            </div>
                            <p className="text-sm text-slate-600">{nota.motivo}</p>
                            <p className="text-xs text-slate-400 mt-2">
                              {nota.docente_nome} - {new Date(nota.created_at).toLocaleDateString('it-IT')}
                            </p>
                          </CardContent>
                        </Card>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          )}
        </TabsContent>

        {/* Lezioni */}
        <TabsContent value="lezioni" className="mt-6">
          {lezioni.length === 0 ? (
            <Card className="border-dashed">
              <CardContent className="flex flex-col items-center justify-center py-12">
                <BookOpen className="w-12 h-12 text-slate-300 mb-4" />
                <p className="text-slate-500">Nessuna lezione registrata</p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-4">
              {lezioni.map((lezione) => (
                <Card key={lezione.id} className="hover:shadow-md transition-shadow">
                  <CardContent className="p-4">
                    <div className="flex justify-between items-start">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <Badge className="bg-blue-100 text-blue-800">{lezione.materia}</Badge>
                          <span className="text-sm text-slate-500 flex items-center gap-1">
                            <Calendar className="w-3 h-3" />
                            {new Date(lezione.data_lezione).toLocaleDateString('it-IT')}
                          </span>
                        </div>
                        <p className="font-medium text-slate-900">{lezione.argomento}</p>
                        {lezione.note && (
                          <p className="text-sm text-slate-600 mt-1">{lezione.note}</p>
                        )}
                        <p className="text-xs text-slate-400 mt-2 flex items-center gap-1">
                          <User className="w-3 h-3" />
                          {lezione.docente_nome}
                        </p>
                      </div>
                      {canDirezione() && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDeleteLezione(lezione.id)}
                          className="text-red-600 hover:text-red-700"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        {/* Voti (per staff) */}
        {canGestisciVoti() && (
          <TabsContent value="voti" className="mt-6">
            {voti.length === 0 ? (
              <Card className="border-dashed">
                <CardContent className="flex flex-col items-center justify-center py-12">
                  <GraduationCap className="w-12 h-12 text-slate-300 mb-4" />
                  <p className="text-slate-500">Nessun voto registrato</p>
                </CardContent>
              </Card>
            ) : (
              <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                {voti.map((voto) => (
                  <Card key={voto.id} className="hover:shadow-md transition-shadow">
                    <CardHeader className="pb-2">
                      <div className="flex justify-between items-start">
                        <div>
                          <CardTitle className="text-base">{voto.studente_nome}</CardTitle>
                          <CardDescription>{voto.materia}</CardDescription>
                        </div>
                        <Badge className={voto.voto >= 6 ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}>
                          {voto.voto}
                        </Badge>
                      </div>
                    </CardHeader>
                    <CardContent>
                      <p className="text-sm text-slate-600">{voto.descrizione}</p>
                      <div className="flex items-center gap-2 mt-2 text-xs text-slate-400">
                        <Clock className="w-3 h-3" />
                        {new Date(voto.data_voto).toLocaleDateString('it-IT')}
                        <span>•</span>
                        <User className="w-3 h-3" />
                        {voto.docente_nome}
                      </div>
                      {canDirezione() && (
                        <div className="flex gap-2 mt-3 pt-3 border-t">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => {
                              setSelectedVoto({...voto});
                              setEditVotoDialogOpen(true);
                            }}
                          >
                            <Edit className="w-3 h-3 mr-1" />
                            Modifica
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            className="text-red-600"
                            onClick={() => handleDeleteVoto(voto.id)}
                          >
                            <Trash2 className="w-3 h-3" />
                          </Button>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>
        )}

        {/* Note Disciplinari (per staff) */}
        {canGestisciVoti() && (
          <TabsContent value="disciplina" className="mt-6">
            {noteDisciplinari.length === 0 ? (
              <Card className="border-dashed">
                <CardContent className="flex flex-col items-center justify-center py-12">
                  <AlertTriangle className="w-12 h-12 text-slate-300 mb-4" />
                  <p className="text-slate-500">Nessuna nota disciplinare</p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-4">
                {noteDisciplinari.map((nota) => {
                  const tipoConfig = TIPI_NOTA.find(t => t.value === nota.tipo);
                  return (
                    <Card key={nota.id} className={`hover:shadow-md transition-shadow ${nota.stato === 'REVOCATA' ? 'opacity-60' : ''}`}>
                      <CardContent className="p-4">
                        <div className="flex justify-between items-start">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-2">
                              <Badge className={tipoConfig?.color || 'bg-slate-100'}>
                                {tipoConfig?.label || nota.tipo}
                              </Badge>
                              <Badge className={STATO_NOTA[nota.stato] || 'bg-slate-100'}>
                                {nota.stato}
                              </Badge>
                              {nota.giorni_sospensione && (
                                <Badge className="bg-red-100 text-red-800">
                                  {nota.giorni_sospensione} giorni
                                </Badge>
                              )}
                            </div>
                            <p className="font-medium text-slate-900">{nota.studente_nome}</p>
                            <p className="text-sm text-slate-600 mt-1">{nota.motivo}</p>
                            <p className="text-xs text-slate-400 mt-2">
                              {nota.docente_nome} - {new Date(nota.created_at).toLocaleDateString('it-IT')}
                            </p>
                          </div>
                          {canDirezione() && nota.stato === 'ATTIVA' && (
                            <div className="flex gap-2">
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => handleRevokeNota(nota.id)}
                              >
                                Revoca
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleDeleteNota(nota.id)}
                                className="text-red-600"
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          )}
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            )}
          </TabsContent>
        )}
      </Tabs>

      {/* Dialog Modifica Voto */}
      <Dialog open={editVotoDialogOpen} onOpenChange={setEditVotoDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Modifica Voto</DialogTitle>
          </DialogHeader>
          {selectedVoto && (
            <form onSubmit={handleEditVoto} className="space-y-4">
              <div className="bg-slate-50 p-3 rounded-md">
                <p className="text-sm text-slate-500">Studente</p>
                <p className="font-medium">{selectedVoto.studente_nome}</p>
              </div>
              <div className="space-y-2">
                <Label>Voto</Label>
                <Input
                  type="number"
                  min="1"
                  max="10"
                  step="0.5"
                  value={selectedVoto.voto}
                  onChange={(e) => setSelectedVoto({...selectedVoto, voto: e.target.value})}
                />
              </div>
              <div className="space-y-2">
                <Label>Descrizione</Label>
                <Input
                  value={selectedVoto.descrizione}
                  onChange={(e) => setSelectedVoto({...selectedVoto, descrizione: e.target.value})}
                />
              </div>
              <div className="flex justify-end gap-2">
                <Button type="button" variant="outline" onClick={() => setEditVotoDialogOpen(false)}>Annulla</Button>
                <Button type="submit">Salva</Button>
              </div>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Scuola;
